import {ActionButton, ActionButtonProps} from './ActionButton'
import React, {ReactNode, useCallback, useState} from 'react'

import {CommonModal} from './CommonModal'
import {useGLTranslation} from './hooks'

interface ActionModalButtonProps {
  /** Message to display with "submit" and "cancel" in a modal before calling onClick. */
  confirmationMessage?: ReactNode
  /** Word the user has to type before "submit" works, for an action a misclick shouldn't reach. */
  confirmationPhrase?: string
}

export const ActionModalButton = ({
  confirmationMessage,
  confirmationPhrase,
  onClick,
  ...otherProps
}: ActionModalButtonProps & ActionButtonProps) => {
  const [isConfirmOpen, setConfirmOpen] = useState(false)
  const [typed, setTyped] = useState('')
  const {t} = useGLTranslation()
  const isConfirmed = confirmationPhrase === undefined || typed === confirmationPhrase
  const confirmationModal = isConfirmOpen && (
    <CommonModal
      onClose={() => setConfirmOpen(false)}
      buttons={[
        {
          text: t('modalCancel'),
          appearance: 'filled',
          size: 'medium',
          onClick: () => setConfirmOpen(false)
        },
        {
          text: t('Submit'),
          appearance: 'filled',
          size: 'medium',
          disabled: !isConfirmed,
          onClick: (e) => {
            onClick && onClick(e)
            setConfirmOpen(false)
            setTyped('')
          }
        }
      ]}
    >
      {confirmationMessage}
      {confirmationPhrase !== undefined && (
        <input
          aria-label={`Type ${confirmationPhrase} to confirm`}
          placeholder={confirmationPhrase}
          value={typed}
          onChange={(event) => setTyped(event.target.value)}
        />
      )}
    </CommonModal>
  )

  const onClickInternal = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      if (confirmationMessage) {
        setTyped('')
        setConfirmOpen(true)
      } else if (onClick) {
        onClick(e)
      }
    },
    [onClick, confirmationMessage]
  )

  return (
    <>
      {confirmationModal}
      <ActionButton onClick={onClickInternal} {...otherProps} />
    </>
  )
}
